import { locationsGroups } from "@utils/constants";

import { Small, Table } from "@fronterahealth/frontera-ui-components";
import { Checkbox } from "@fronterahealth/frontera-ui-components/ui/checkbox";
import { Input } from "@fronterahealth/frontera-ui-components/ui/input";
import { Label } from "@fronterahealth/frontera-ui-components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@fronterahealth/frontera-ui-components/ui/select";

import { getFieldKey, parseJson } from "@components/FieldComponent/utils";
import { ReportFieldInputProps } from "@components/ReportField/types";
import { useAdminData } from "@providers/AdminDataProvider";

export const OTHER_SETTING_LOCATION = "Other Setting";

export interface PractitionerDataRow {
  id: number;
  index: number;
  level: string;
  hour: number | null;
  locationOfService: string[];
  isRange: boolean;
  rangeMin: number | null;
  rangeMax: number | null;
  hasSecondRange: boolean;
  secondRangeMin?: number | null;
  secondRangeMax?: number | null;
  otherSettingDescription?: string;
}

const getDefaultData = (canAddSecondRange: boolean): PractitionerDataRow[] => [
  {
    id: 1,
    index: 0,
    level: "Paraprofessional",
    hour: 0,
    locationOfService: [],
    isRange: canAddSecondRange,
    rangeMin: 0,
    rangeMax: 0,
    hasSecondRange: canAddSecondRange,
    secondRangeMin: canAddSecondRange ? 0 : undefined,
    secondRangeMax: canAddSecondRange ? 0 : undefined,
    otherSettingDescription: "",
  },
  {
    id: 2,
    index: 1,
    level: "Social Skills Group",
    hour: 0,
    locationOfService: [],
    isRange: canAddSecondRange,
    rangeMin: 0,
    rangeMax: 0,
    hasSecondRange: canAddSecondRange,
    secondRangeMin: canAddSecondRange ? 0 : undefined,
    secondRangeMax: canAddSecondRange ? 0 : undefined,
    otherSettingDescription: "",
  },
  {
    id: 3,
    index: 2,
    level: "Mid-Level Supervisor",
    hour: 0,
    locationOfService: [],
    isRange: canAddSecondRange,
    rangeMin: 0,
    rangeMax: 0,
    hasSecondRange: false,
    otherSettingDescription: "",
  },
  {
    id: 4,
    index: 3,
    level: "High Level Supervisor",
    hour: 0,
    locationOfService: [],
    isRange: canAddSecondRange,
    rangeMin: 0,
    rangeMax: 0,
    hasSecondRange: false,
    otherSettingDescription: "",
  },
];

const getLocationsByIndex = (index: number): string[] => locationsGroups[index] ?? [];

const getRows = (value: string | null | undefined, canAddSecondRange: boolean): PractitionerDataRow[] => {
  const savedRows = parseJson<PractitionerDataRow[]>(value) ?? [];
  return getDefaultData(canAddSecondRange).map((defaultRow) => {
    const savedRow = savedRows.find((row) => row.id === defaultRow.id);
    return { ...defaultRow, ...savedRow };
  });
};

export const ReportFieldTreatmentOptionTable: React.FC<ReportFieldInputProps> = ({
  field,
  value,
  onChange,
  disabled,
}) => {
  const { isAcesUser } = useAdminData();
  const fieldKey = getFieldKey(field) ?? "";
  const rows = getRows(value, isAcesUser);

  const saveRows = (updatedRows: PractitionerDataRow[]) => onChange(JSON.stringify(updatedRows));

  const updateRow = (rowId: number, updates: Partial<PractitionerDataRow>) => {
    saveRows(rows.map((row) => (row.id === rowId ? { ...row, ...updates } : row)));
  };

  const toggleLocation = (row: PractitionerDataRow, loc: string, checked: boolean) => {
    const currentLocations = row.locationOfService || [];
    const updatedLocations = checked ? [...currentLocations, loc] : currentLocations.filter((l) => l !== loc);
    const updates: Partial<PractitionerDataRow> = { locationOfService: updatedLocations };
    // Clear on both check AND uncheck so a stale otherSettingDescription
    // (e.g., persisted on the row while Other Setting was unchecked) can't
    // resurface in the inline input or auto-save behind the clinician.
    if (loc === OTHER_SETTING_LOCATION) {
      updates.otherSettingDescription = "";
    }
    updateRow(row.id, updates);
  };

  return (
    <Table
      defaultItemsPerPage={20}
      cellClassName="cursor-default"
      data={rows}
      columns={[
        {
          dataIndex: "level",
          key: "level",
          title: "Level",
          className: "border-r align-top w-1/3",
          render: (_, record) => <div className="p-2 font-medium">{record.level}</div>,
        },
        {
          dataIndex: "hour",
          key: "hour",
          title: isAcesUser ? "Hours" : "Hour/Month",
          className: "border-r align-top w-1/3",
          render: (_, row) => {
            const isError = row.isRange && row.rangeMin != null && row.rangeMax != null && row.rangeMin > row.rangeMax;

            const isSecondRangeError =
              row.isRange &&
              row.hasSecondRange &&
              row.secondRangeMin != null &&
              row.secondRangeMax != null &&
              row.secondRangeMin > row.secondRangeMax;

            if (disabled) {
              let displayValue: string;
              if (!row.isRange) {
                displayValue = `${row.hour ?? "_"} Hour${row.hour && row.hour > 1 ? "s" : ""}`;
              } else if (isError) {
                displayValue = "Invalid range - min > max";
              } else {
                displayValue = `${row.rangeMin ?? "_"} - ${row.rangeMax ?? "_"} Hours/Month`;
              }

              let secondDisplayValue: string | null = null;
              if (row.hasSecondRange) {
                secondDisplayValue = isSecondRangeError
                  ? "Invalid range - min > max"
                  : `${row.secondRangeMin ?? "_"} - ${row.secondRangeMax ?? "_"} Hours/Week`;
              }

              return (
                <>
                  <div className="p-2">{displayValue}</div>
                  {secondDisplayValue && <div className="p-2">{secondDisplayValue}</div>}
                </>
              );
            }

            const items = [
              { label: "Exact Hours", value: "exact" },
              { label: row.hasSecondRange ? "Hour Ranges" : "Hour Range", value: "range" },
            ];

            return (
              <div className="flex flex-col gap-3 p-2">
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`hour-type-${row.id}`} className="text-sm font-medium text-text-secondary">
                    Hour Type
                  </Label>
                  <Select
                    items={items}
                    value={row.isRange ? "range" : "exact"}
                    onValueChange={(next) => {
                      const isRange = next === "range";
                      updateRow(row.id, {
                        isRange,
                        hour: isRange ? null : row.hour,
                        rangeMin: isRange ? row.rangeMin : null,
                        rangeMax: isRange ? row.rangeMax : null,
                        secondRangeMin: isRange && row.hasSecondRange ? row.secondRangeMin : null,
                        secondRangeMax: isRange && row.hasSecondRange ? row.secondRangeMax : null,
                      });
                    }}
                  >
                    <SelectTrigger id={`hour-type-${row.id}`} className="w-full">
                      <SelectValue placeholder="Theme" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {items.map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                {row.isRange ? (
                  <div className="space-y-3">
                    {row.hasSecondRange && <div className="text-sm font-medium text-text-secondary">Hours/Month</div>}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <Label htmlFor={`range-min-${row.id}`} className="text-sm text-text-secondary">
                          Min
                        </Label>
                        <Input
                          id={`range-min-${row.id}`}
                          name={`range-min-${row.id}`}
                          type="number"
                          min={0}
                          max={160}
                          step="0.1"
                          value={row.rangeMin ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateRow(row.id, { rangeMin: value === "" ? null : parseFloat(value) });
                          }}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label htmlFor={`range-max-${row.id}`} className="text-sm text-text-secondary">
                          Max
                        </Label>
                        <Input
                          id={`range-max-${row.id}`}
                          name={`range-max-${row.id}`}
                          type="number"
                          min={0}
                          max={160}
                          step="0.1"
                          value={row.rangeMax ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateRow(row.id, { rangeMax: value === "" ? null : parseFloat(value) });
                          }}
                        />
                      </div>
                    </div>
                    {isError && (
                      <Small className="text-text-error-primary">
                        The minimum value must be less than the maximum value.
                      </Small>
                    )}
                    {row.hasSecondRange && (
                      <>
                        <div className="text-sm font-medium text-text-secondary">Hours/Week</div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex flex-col gap-1">
                            <Label htmlFor={`range-2-min-${row.id}`} className="text-sm text-text-secondary">
                              Min
                            </Label>
                            <Input
                              id={`range-2-min-${row.id}`}
                              name={`range-2-min-${row.id}`}
                              type="number"
                              min={0}
                              max={160}
                              step="0.1"
                              value={row.secondRangeMin ?? ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                updateRow(row.id, { secondRangeMin: value === "" ? null : parseFloat(value) });
                              }}
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <Label htmlFor={`range-2-max-${row.id}`} className="text-sm text-text-secondary">
                              Max
                            </Label>
                            <Input
                              id={`range-2-max-${row.id}`}
                              name={`range-2-max-${row.id}`}
                              type="number"
                              min={0}
                              max={160}
                              step="0.1"
                              value={row.secondRangeMax ?? ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                updateRow(row.id, { secondRangeMax: value === "" ? null : parseFloat(value) });
                              }}
                            />
                          </div>
                        </div>
                        {isSecondRangeError && (
                          <Small className="text-text-error-primary">
                            The minimum value must be less than the maximum value.
                          </Small>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    <Label htmlFor={`hour-${row.id}`} className="text-sm text-text-secondary">
                      Hours
                    </Label>
                    <Input
                      id={`hour-${row.id}`}
                      name={`hour-${row.id}`}
                      type="number"
                      min={0}
                      max={160}
                      step="0.1"
                      value={row.hour ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        updateRow(row.id, { hour: value === "" ? null : parseFloat(value) });
                      }}
                    />
                  </div>
                )}
              </div>
            );
          },
        },
        {
          dataIndex: "locationOfService",
          key: "locationOfService",
          title: "Service Location",
          className: "align-top w-1/3",
          render: (_, record) => {
            const locations = getLocationsByIndex(record.index);
            const picked = record.locationOfService ?? [];
            return (
              <div className="space-y-2 p-2">
                {locations.map((loc) => {
                  const id = `row-${record.index}-${loc}-${fieldKey}`;
                  const isChecked = picked.includes(loc);
                  const isOther = loc === OTHER_SETTING_LOCATION;
                  return (
                    <div key={id} className="flex items-center gap-3">
                      <Checkbox
                        id={id}
                        checked={isChecked}
                        disabled={disabled}
                        onCheckedChange={(checked) => toggleLocation(record, loc, checked === true)}
                      />
                      <Label htmlFor={id} className="text-sm whitespace-nowrap text-text-secondary">
                        {loc}
                      </Label>
                      {isOther && isChecked && (
                        <>
                          <span aria-hidden="true" className="-ml-3 text-sm text-text-secondary">
                            :
                          </span>
                          {disabled ? (
                            <Small className="text-text-secondary line-clamp-1">
                              {record.otherSettingDescription || "—"}
                            </Small>
                          ) : (
                            <Input
                              type="text"
                              name={`other-setting-${record.id}-${fieldKey}`}
                              aria-label="Other Setting address or description"
                              value={record.otherSettingDescription ?? ""}
                              onChange={(e) => updateRow(record.id, { otherSettingDescription: e.target.value })}
                              className="h-6 w-0 max-w-full -mx-2 -my-1 flex-1 px-2 py-0 text-sm"
                            />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          },
        },
      ]}
    />
  );
};
